import React from 'react';
import { Button, Table } from 'antd';
import type { TableProps } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import './ObjectCard.css';

/**
 * Object card primitives (customer, supplier, product, category): a hero with
 * monogram, kind, name, meta line and a strip of facts; then a two-column body
 * of sections (main left, facts aside right). Rules: components/layout/DESIGN.md
 * and modules/orders/process/DESIGN.md „Karta obiektu”. Owner 2026-10-19:
 * „bardzo nowocześnie”.
 */

/** Plain text of a value for a `title` tooltip when one line cuts it. */
const textOf = (node: React.ReactNode): string | undefined => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const text = node.map(textOf).filter(Boolean).join('');
    return text || undefined;
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children);
  return undefined;
};

const isLetter = (char: string) => char.toLowerCase() !== char.toUpperCase();

/** Two initials of the name, preferring words that start with a letter (codes like „REVIEW-123” are skipped). */
export const initialsOf = (name: string) => {
  const words = name.split(/[\s\-_/.,()]+/).filter(Boolean);
  const lettered = words.filter((word) => isLetter(word[0]) && !/\d/.test(word));
  const source = lettered.length ? lettered : words;
  return source.slice(0, 2).map((word) => word[0]).join('').toUpperCase() || '·';
};

/** Colour of an icon tile (Apple-style settings icon: white glyph on a colour tile). */
export type ObjectCardTone = 'blue' | 'indigo' | 'green' | 'orange' | 'pink' | 'teal' | 'gray';

const FACT_TONES: ObjectCardTone[] = ['blue', 'green', 'orange', 'indigo', 'teal', 'pink'];

/** Icon tile used by facts, sections and empty states. */
export const ObjectCardTile: React.FC<{ tone?: ObjectCardTone; size?: 'sm' | 'md'; children: React.ReactNode }> = ({
  tone = 'blue', size = 'md', children,
}) => (
  <span className={`obj-tile obj-tile--${tone} obj-tile--${size}`} aria-hidden="true">{children}</span>
);

export interface ObjectCardFact {
  key: string;
  label: string;
  icon?: React.ReactNode;
  tone?: ObjectCardTone;
  value: React.ReactNode;
  testId?: string;
}

interface ObjectCardHeroProps {
  /** Business kind shown above the name, e.g. „Dostawca”. */
  kind: string;
  name: React.ReactNode;
  /** Plain name used for the monogram when `name` is a node. */
  monogramFrom?: string;
  /** Replaces the monogram (e.g. a category colour tag swatch). */
  avatar?: React.ReactNode;
  backLabel: string;
  onBack: () => void;
  /** Pills/tags next to the name (status, branch, data gaps). */
  badges?: React.ReactNode;
  /** Muted line under the name joined with „ · ”. */
  meta?: React.ReactNode[];
  actions?: React.ReactNode;
  facts?: ObjectCardFact[];
  alerts?: React.ReactNode;
  testId?: string;
}

export const ObjectCardHero: React.FC<ObjectCardHeroProps> = ({
  kind, name, monogramFrom, avatar, backLabel, onBack, badges, meta, actions, facts, alerts, testId,
}) => {
  const metaItems = (meta || []).filter((item) => item !== null && item !== undefined && item !== false && item !== '');
  return (
    <section className="obj-hero" data-testid={testId}>
      <div className="obj-hero__top">
        <Button type="text" size="small" className="obj-hero__back" icon={<ArrowLeftOutlined />} onClick={onBack}>
          {backLabel}
        </Button>
        {actions && <div className="obj-hero__actions">{actions}</div>}
      </div>
      <div className="obj-hero__identity">
        <div className="obj-hero__avatar" aria-hidden="true">
          {avatar ?? initialsOf(monogramFrom ?? (typeof name === 'string' ? name : ''))}
        </div>
        <div className="obj-hero__copy">
          <span className="obj-hero__kind">{kind}</span>
          <div className="obj-hero__title-row">
            <h1 className="obj-hero__name" title={textOf(name) ?? monogramFrom}>{name}</h1>
            {badges}
          </div>
          {metaItems.length > 0 && (
            <div className="obj-hero__meta">
              {metaItems.map((item, index) => (
                <span key={index} className="obj-hero__meta-item" title={textOf(item)}>{item}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {facts && facts.length > 0 && (
        <dl className="obj-hero__facts">
          {facts.map((fact, index) => (
            <div className="obj-fact" key={fact.key} data-testid={fact.testId}>
              {fact.icon && <ObjectCardTile tone={fact.tone ?? FACT_TONES[index % FACT_TONES.length]}>{fact.icon}</ObjectCardTile>}
              <div className="obj-fact__copy">
                <dt>{fact.label}</dt>
                <dd title={textOf(fact.value)}>{fact.value}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}
      {alerts && <div className="obj-hero__alerts">{alerts}</div>}
    </section>
  );
};

interface ObjectCardSectionProps {
  title: string;
  icon?: React.ReactNode;
  tone?: ObjectCardTone;
  /** Small count badge next to the title (a number, or e.g. „10+” for a capped list). */
  count?: number | string;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  id?: string;
  className?: string;
  sectionRef?: React.Ref<HTMLElement>;
  /** Spans both columns of `obj-card__body`. */
  wide?: boolean;
}

export const ObjectCardSection: React.FC<ObjectCardSectionProps> = ({
  title, icon, tone, count, hint, actions, children, testId, id, className, sectionRef, wide,
}) => (
  <section
    className={`obj-section${wide ? ' obj-section--wide' : ''}${className ? ` ${className}` : ''}`}
    data-testid={testId}
    id={id}
    ref={sectionRef}
  >
    <header className="obj-section__header">
      {icon && <ObjectCardTile tone={tone} size="sm">{icon}</ObjectCardTile>}
      <h2 className="obj-section__title">{title}</h2>
      {count !== undefined && <span className="obj-section__count">{count}</span>}
      {actions && <div className="obj-section__actions">{actions}</div>}
    </header>
    {hint && <p className="obj-section__hint" title={textOf(hint)}>{hint}</p>}
    {children}
  </section>
);

/** Facts list for the aside: label above value, one per row. */
export const ObjectCardFacts: React.FC<{ items: Array<{ label: string; value: React.ReactNode }> }> = ({ items }) => (
  <dl className="obj-facts">
    {items.map((item) => (
      <div className="obj-facts__row" key={item.label}>
        <dt>{item.label}</dt>
        <dd title={textOf(item.value)}>{item.value}</dd>
      </div>
    ))}
  </dl>
);

/** Empty collection: icon, one sentence and the one action that fills it. */
export const ObjectCardEmpty: React.FC<{ icon?: React.ReactNode; text: React.ReactNode; action?: React.ReactNode }> = ({
  icon, text, action,
}) => (
  <div className="obj-empty">
    {icon && <span className="obj-empty__icon" aria-hidden="true">{icon}</span>}
    <span className="obj-empty__text" title={textOf(text)}>{text}</span>
    {action}
  </div>
);

/**
 * Table inside an object-card section: fixed column layout (antd sets
 * `table-layout` inline, so CSS alone cannot), one line per cell with an
 * ellipsis and the full text in `title`, small size, no pagination.
 */
export function ObjectCardTable<T extends object>({ columns, ...props }: TableProps<T>) {
  const oneLineColumns = (columns || []).map((column) => {
    const render = (column as { render?: (value: unknown, record: T, index: number) => React.ReactNode }).render;
    const dataIndex = (column as { dataIndex?: keyof T }).dataIndex;
    return {
      ...column,
      render: (value: unknown, record: T, index: number) => {
        const content = render
          ? render(value, record, index)
          : (dataIndex !== undefined ? (record[dataIndex] as React.ReactNode) : (value as React.ReactNode));
        return <span className="obj-cell" title={textOf(content)}>{content}</span>;
      },
    };
  });
  return (
    <Table<T>
      size="small"
      pagination={false}
      tableLayout="fixed"
      {...props}
      columns={oneLineColumns as TableProps<T>['columns']}
    />
  );
}
